import * as _ from 'lodash';
import { Component } from 'react';
import type { RouteComponentProps } from 'react-router';
import type { SortingRule, Column } from 'react-table';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import Header from '../../../shared/settings/SettingsPageHeader';
import Loader from '../../../generic/Loader';
import PopperMenu from '../../../generic/PopperMenu';
import SearchInput from '../../../generic/SearchInput';
import SimpleReactTable from '../../../generic/SimpleReactTable';
import TableHeader from '../../../generic/TableHeader';
import type { RolesStore } from '../../../../redux/roles';
import RolesModule from '../../../../redux/roles';
import type { IAsyncStatus } from '../../../../types';
import type { Role } from '../../../../../typings/api';
import { window } from '../../../../globals';
import { Action, ResourceType } from '../../../../../typings/permissions';
import { WithDialogContext } from '../../../../contexts/DialogContext';
import type { DialogState } from '../../../../contexts/DialogContext';

type ReduxStateProps = {
  roles: RolesStore['entities'];
  status: IAsyncStatus;
};

type DispatchProps = {
  fetchRoles: () => void;
  deleteRole: (roleId: number) => void;
  cloneRole: (role: Partial<Role>) => void;
};

type OrganizationRolesIndexProps = RouteComponentProps<object, object> & ReduxStateProps & DispatchProps & {
  dialogContext: DialogState;
};

type OrganizationRolesIndexState = {
  sortMode: SortingRule;
  searchValue: string;
};

class OrganizationRolesIndex extends Component<OrganizationRolesIndexProps, OrganizationRolesIndexState> {
  readonly state: OrganizationRolesIndexState = {
    sortMode: {
      id: 'name',
      desc: false,
    },
    searchValue: '',
  };

  componentDidMount() {
    this.props.fetchRoles();
  }

  get userCanManageRoles() {
    return window.hasPermission({
      resourceType: ResourceType.Role,
      action: Action.Manage,
    });
  }

  onSortedChanged = (sortMode: SortingRule) => this.setState({ sortMode });

  //  Returns an onClick handler which brings us to the edit page, which will handle grabbing the rest of the role data from Redux.
  onEditRoleClick = (role: Role) => () => this.navigateToEditPage(role.id);

  onAddRoleClick = () => this.navigateToEditPage('add');

  onCloneRoleClick =
    ({ name, description, scope, permissions }: Role) =>
      () => {
        const { cloneRole } = this.props;
        cloneRole({
          id: 'add',
          name: `${name} (copy)`,
          description,
          scope,
          permissions,
          isCustom: true,
        });
        this.navigateToEditPage('clone');
      };

  getColumns = (): Array<Column<any>> => {
    const { sortMode } = this.state;

    return [
      {
        id: 'name',
        Header: <TableHeader title="Role" sort={{ id: 'name', currentMode: sortMode }} />,
        accessor: 'name',
        width: 310,
        resizable: false,
        style: {
          textOverflow: 'ellipsis',
        },
      },
      {
        id: 'description',
        Header: <TableHeader title="Description" sort={{ id: 'description', currentMode: sortMode }} />,
        className: 'role-settings__table__cell',
        // eslint-disable-next-line react/no-unstable-nested-components
        Cell: ({ value, original: role }) => (
          <>
            <span className="role-settings__table__cell__text" title={value}>
              {value}
            </span>
            {this.userCanManageRoles && (
              <PopperMenu>
                <PopperMenu.Item onClick={this.onEditRoleClick(role)}>
                  {role.isCustom ? 'Edit' : 'View'} role
                </PopperMenu.Item>
                <PopperMenu.Item onClick={this.onCloneRoleClick(role)}>Duplicate role</PopperMenu.Item>
                {role.isCustom && (
                  <PopperMenu.Item onClick={this.onRemoveRoleClick(role)} isWarning>
                    Remove role
                  </PopperMenu.Item>
                )}
              </PopperMenu>
            )}
          </>
        ),
        accessor: 'description',
        resizable: false,
        style: { textOverflow: 'ellipsis', paddingRight: '15px' },
      },
    ];
  };

  get filteredRoles() {
    const roles = Object.values(this.props.roles);
    const { searchValue } = this.state;

    if (searchValue) {
      const searchLower = searchValue.toLowerCase();
      return roles.filter(
        (role: Role) =>
          role.name.toLowerCase().includes(searchLower) || role.description.toLowerCase().includes(searchLower),
      );
    }

    return roles;
  }

  get shouldShowLoader() {
    const { status, roles } = this.props;
    return status.loading || _.isEmpty(roles);
  }

  navigateToEditPage = (roleId: number | string): void => {
    const { router } = this.props;
    router.push(`/account/settings/organization/roles/${roleId}`);
  };

  onRemoveRoleClick = (role: Role) => () =>
    this.props.dialogContext.onConfirm({
      title: 'Remove Role?',
      body: `Are you sure you want to remove the "${role.name}" role?`,
      onConfirm: () => this.props.deleteRole(role.id),
      confirmText: 'Remove',
      confirmBtnType: 'error',
    });

  render() {
    if (this.shouldShowLoader) {
      return (
        <div className="role-settings__loader">
          <Loader />
        </div>
      );
    }
    const { searchValue } = this.state;

    const { filteredRoles } = this;

    return (
      <>
        <Header heading="Roles" isPadded>
          {this.userCanManageRoles && (
            <Header.Button type="secondary" size="medium" onClick={this.onAddRoleClick}>
              Add role
            </Header.Button>
          )}
        </Header>
        <div className="role-settings__search-panel">
          <SearchInput value={searchValue} onChange={(ev) => this.setState({ searchValue: ev.target.value })} />
        </div>
        <div className="roles-table">
          <SimpleReactTable
            data={filteredRoles}
            columns={this.getColumns()}
            defaultSortMode={[
              {
                id: 'name',
                desc: false,
              },
            ]}
            onSortedChanged={this.onSortedChanged}
            showPagination={filteredRoles.length > 20}
            cacheId="organizationRoles"
          />
        </div>
      </>
    );
  }
}

const mapStateToProps = createStructuredSelector({
  roles: RolesModule.selectors.getRoles,
  status: RolesModule.selectors.getStatus,
});

const mapDispatchToProps: DispatchProps = {
  fetchRoles: RolesModule.actions.fetchAll,
  deleteRole: RolesModule.actions.delete,
  cloneRole: RolesModule.actions.cloneRole,
};

export default connect(mapStateToProps, mapDispatchToProps)(
  WithDialogContext(OrganizationRolesIndex),
);
